/** Operator-facing labels — read-only case summary; no payment/receipt/settlement wording. */

import type { ArEntryStatus } from '@shared/finance/ar-entry-types';
import type { CollectionCaseStatus } from '@shared/collection/collection-case-types';
import type { CollectionStage } from '@shared/finance/ar-collection-aging-types';

export const COLLECTION_CASE_SUMMARY_OPERATOR_LABELS = {
  panelTitle: '수금 케이스 요약',
  panelSubtitle: '참고 · 관찰',
  boundaryPurpose: '요약은 현재 AR 상태와 팔로업 기록을 읽어 보여주는 정보입니다.',
  boundaryNoAutomation: '자동 연락, 법적 조치, 수금 처리 또는 영수증 발행을 실행하지 않습니다.',
  boundaryHumanGated: '후속 조치는 담당자가 직접 판단합니다.',
  retryAction: '다시 불러오기',
  loadFailed: '케이스 요약을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
  loading: '케이스 요약을 불러오는 중…',
  missingArEntry:
    '선택한 항목에서 AR ID를 확인할 수 없습니다. 미수금(AR) 목록에서 다시 선택해 주세요.',
  footerLiveRead: '실시간 조회 · 자동 조치 없음',
  footerAsOfPrefix: '조회 시점',
  sectionAr: '미수(AR)',
  sectionAging: '연체·에이징',
  sectionCase: '수금 케이스',
  sectionContacts: '연락 활동',
  sectionCommitments: '약속',
  outstandingAmount: '미수 잔액',
  arStatus: 'AR 상태',
  dueDate: '결제 기한',
  actionableCapability: '조치 가능',
  actionableHint: '약속·연락 가능',
  notActionableHint: '조치 불가',
  daysPastDue: '연체 일수',
  overdueTag: '연체',
  notOverdueTag: '연체 아님',
  suggestedStage: '권장 단계',
  effectiveDueDate: '기준 만기일',
  caseStatus: '케이스 상태',
  caseStage: '현재 단계',
  noCollectionCase: '등록된 수금 케이스 없음',
  contactAttemptsTotal: '연락 시도',
  contactAttemptsActive: '진행 중',
  contactAttemptsCompleted: '완료',
  contactAttemptsCancelled: '취소·실패',
  activeContactFlowPrefix: '진행 중인 연락 흐름',
  commitmentTotal: '약속 건수',
  commitmentPending: '대기 중',
  commitmentBrokenObservation: '미이행 (관찰)',
  emptyValue: '—',
} as const;

const AR_STATUS_LABELS: Record<ArEntryStatus, string> = {
  open: '미결',
  partially_paid: '부분 수납',
  paid: '완료',
  written_off: '상각',
};

const CASE_STATUS_LABELS: Record<CollectionCaseStatus, string> = {
  open: '진행',
  committed: '약속 있음',
  resolved: '해결',
  escalated: '에스컬레이션',
  closed: '종료',
};

const COLLECTION_STAGE_LABELS: Record<CollectionStage, string> = {
  reminder: '리마인더',
  visit: '방문',
  revisit: '재방문',
  final_letter: '최종 통지 (참고)',
  legal_prep: '법무 준비 (참고)',
};

export function labelArEntryStatusForCaseSummary(status: ArEntryStatus): string {
  return AR_STATUS_LABELS[status] ?? status;
}

export function labelCollectionCaseStatusForCaseSummary(status: CollectionCaseStatus): string {
  return CASE_STATUS_LABELS[status] ?? status;
}

export function labelCollectionStageForCaseSummary(stage: string | null | undefined): string {
  if (!stage?.trim()) {
    return COLLECTION_CASE_SUMMARY_OPERATOR_LABELS.emptyValue;
  }
  const normalized = stage.trim() as CollectionStage;
  return COLLECTION_STAGE_LABELS[normalized] ?? stage;
}

export function formatCaseSummaryFooter(asOf: string): string {
  const formatted = formatCaseSummaryTimestamp(asOf);
  return `${COLLECTION_CASE_SUMMARY_OPERATOR_LABELS.footerAsOfPrefix}: ${formatted} · ${COLLECTION_CASE_SUMMARY_OPERATOR_LABELS.footerLiveRead}`;
}

export function formatCaseSummaryTimestamp(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) {
    return iso;
  }
  return new Intl.DateTimeFormat('ko-KR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(parsed));
}

export function formatOutstandingAmountForCaseSummary(amount: string, currency: string): string {
  const trimmed = amount.trim();
  if (!trimmed) {
    return COLLECTION_CASE_SUMMARY_OPERATOR_LABELS.emptyValue;
  }
  const numeric = Number(trimmed);
  if (Number.isFinite(numeric)) {
    return `${numeric.toLocaleString('ko-KR')} ${currency.trim() || ''}`.trim();
  }
  return `${trimmed} ${currency.trim()}`.trim();
}
